import { z } from 'zod';

export const ModelSchema = z.object({
  modelId: z.string().uuid('Model name is required'),
  position: z.array(z.string()).nonempty('Position is required'),
  sizes: z.array(
    z.object({
      sizeId: z.string().uuid('Size name is required'),
      quantity: z
        .union([z.string(), z.number()])
        .refine((value) => !isNaN(Number(value)), {
          message: 'Quantity must be a number',
        })
        .transform((value) => Number(value))
        .refine((val) => val >= 0 && val <= 1000, {
          message: 'Quantity must be 0–1000',
        }),
    })
  ),
});

const PrinterSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  printerId: z.string().min(1, 'Printer is required'),
  type: z.string().min(1, 'Type is required'),
});

const StyleSchema = z.object({
  styleId: z.string().uuid('Style is required'),
  imagePublicUrl: z.string().optional(),
});

const TextStyleSchema = z.object({
  textStyleId: z.string().uuid('Text style is required'),
});

const OverlaySchema = z.object({
  overlayId: z.string().uuid('Overlay is required'),
});

const CollarColorSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  hex: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Hex must be a valid color (e.g. #1a2b3c)'),
});

const LacesColorSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  hex: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Hex must be a valid color (e.g. #1a2b3c)'),
  quantity: z
    .union([z.string(), z.number()])
    .refine((value) => !isNaN(Number(value)), {
      message: 'Quantity must be a number',
    })
    .transform((value) => Number(value))
    .refine((val) => val >= 0, { message: 'Quantity must be 0 or more' }),
});

const MaskSchema = z.object({
  maskId: z.string().uuid('Mask is required'),
  overlays: z.array(OverlaySchema).optional(),
});

export const EventFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Event name must be at least 3 characters long')
    .regex(/^[a-zA-Z0-9 ]+$/, {
      message: 'Only letters and numbers are allowed',
    })
    .max(100, 'Event name cannot exceed 100 characters'),
  startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Start date is required',
  }),
  types: z.array(z.string()).min(1, 'At least one type is required'),
  idleStateVideoPublicUrl: z.union([z.string(), z.instanceof(File)]).optional(),
  backgroundImageUrl: z.union([z.string(), z.instanceof(File)]).optional(),
  publicUrl: z.union([
    z.string().min(1, 'Video preview is required'),
    z.instanceof(File),
  ]),
  aiModel: z.string().min(1, 'AI model is required'),
  printers: z.array(PrinterSchema).min(1, 'At least one printer is required'),
  models: z.array(ModelSchema),
  styles: z.array(StyleSchema).min(1, 'At least one style is required'),
  textStyles: z.array(TextStyleSchema).optional(),
  masks: z.array(MaskSchema),
  lacesColors: z.array(LacesColorSchema),
  collarColors: z.array(CollarColorSchema),
  systemPromptCategoryId: z.string().optional(),
  systemPromptId: z.string().optional(),
  systemPromptContent: z.string().optional(),
  systemPromptSurpriseMeContent: z.string().optional(),
  systemPromptNotes: z.string().optional(),
  securityPrompt: z.string().optional(),
  surpriseMeEnabled: z.boolean().optional(),
  isFadedHeelEnabled: z.boolean().optional(),
  isShoeSizeHintEnabled: z.boolean().optional(),
});
